import {
  Navigate,
  type RouteObject,
  useLocation,
  useParams,
} from 'react-router';
import { userSelector } from 'slices/auth.slice';
import { useAppSelector } from 'slices/store';
import { parseCustomerSignature } from 'utils/customerSignature';

/**
 * Old URL prefixes from the legacy router and where they live now, relative to
 * the customer signature. Pages still link to the old URLs, and they work for
 * old bookmarks too. React Router picks the most specific match.
 */
const LEGACY_REDIRECTS: { from: string; to: string }[] = [
  { from: 'configuration', to: 'platform/configuration' },
  // Configuration pages that moved to the scheduling suite
  ...[
    'reservation-templates',
    'reservation-modes',
    'object-types',
    'fields',
    'availability-relations',
    'padding-rules',
    'field-relations',
    'memberships',
    'relation-types',
    'dynamic-availability',
    'periods',
    'time-rules',
  ].map((page) => ({
    from: `configuration/${page}`,
    to: `scheduling/configuration/${page}`,
  })),
  // System preferences pages had no segment of their own in the legacy router
  ...['support-info', 'email-templates', 'time-zones', 'advanced'].map(
    (page) => ({
      from: `configuration/${page}`,
      to: `platform/configuration/system-preferences/${page}`,
    })
  ),
  { from: 'user-management', to: 'platform/user-management' },
  // Same for the log pages
  ...['sso', 'imports', 'reporting-logs'].map((page) => ({
    from: `user-management/${page}`,
    to: `platform/user-management/logs/${page}`,
  })),
  { from: 'api-services', to: 'platform/api-services' },
  { from: 'app-settings', to: '' },
  { from: 'app-settings/integration', to: 'platform/integration' },
  { from: 'app-settings/hub', to: 'platform/hub' },
  { from: 'app-settings/reporting', to: 'platform/reporting' },
  { from: 'app-settings/exam', to: 'scheduling/exam' },
  { from: 'app-settings/exam-management', to: 'scheduling/assessment' },
  { from: 'app-settings/preferences', to: 'scheduling/preferences' },
  { from: 'app-settings/allocate', to: 'scheduling/allocate' },
  { from: 'app-settings/data-manager', to: 'scheduling/data-manager' },
  { from: 'objects', to: 'scheduling/objects' },
];

// Longest first, so the most specific prefix wins like in React Router
const byLongestPrefix = [...LEGACY_REDIRECTS].sort(
  (a, b) => b.from.length - a.from.length
);

/**
 * Converts an old URL path, relative to the customer signature, to where it
 * lives now. Returns the path unchanged if it isn't an old URL.
 *
 * Used when navigating inside the AOP router, so pages linking to their own
 * old URL stay on the same route instead of remounting through a redirect.
 */
export function toSuitePath(path: string) {
  const leadingSlash = path.startsWith('/') ? '/' : '';
  const trimmed = path.replace(/^\/+/, '');
  const redirect = byLongestPrefix.find(
    ({ from }) => trimmed === from || trimmed.startsWith(`${from}/`)
  );
  if (!redirect) return path;

  const rest = trimmed.slice(redirect.from.length).replace(/^\/+/, '');
  return `${leadingSlash}${[redirect.to, rest].filter(Boolean).join('/')}`;
}

/** Keeps the rest of the path, the query string, hash and navigation state */
function LegacyRedirect({ to }: { to: string }) {
  const { '*': rest } = useParams();
  const { search, hash, state } = useLocation();
  const user = useAppSelector(userSelector);
  const customerSignature = parseCustomerSignature(user?.customerSignature);

  const pathname = `/${[customerSignature, to, rest].filter(Boolean).join('/')}`;

  return <Navigate to={{ pathname, search, hash }} state={state} replace />;
}

export const legacyRedirectRoutes: RouteObject[] = LEGACY_REDIRECTS.map(
  ({ from, to }) => ({
    path: `${from}/*`,
    element: <LegacyRedirect to={to} />,
  })
);
